import { useCallback, MutableRefObject } from "react";
import DataRequest from "../../../services/dataRequest";
import { Attribute, Category, Condition, SelectedAttribute } from "../ez-quote.types";
import { isJewelryOnlyBrand } from "../../../constants/brandFeatures";

type CategoryItemState = {
  [categoryId: number]: {
    attributes: { [key: string]: SelectedAttribute };
    condition: Condition | null;
  };
};

type FetchDeps = {
  brand: string | null;
  goldParentCategory: Category | null;
  categoryItemState: CategoryItemState;
  isEditing: boolean;
  categoriesFetchedRef: MutableRefObject<boolean>;
  initialMountRef: MutableRefObject<boolean>;
  restoringCategoryRef: MutableRefObject<Category | null>;
  itemTypeLabelRef: MutableRefObject<string | undefined>;
  setLoading: (v: boolean) => void;
  setError: (v: string | null) => void;
  setCategories: (v: Category[]) => void;
  setCategoryHistory: (v: Category[][]) => void;
  setSelectedCategoryParent: (v: Category | null) => void;
  setSelectedRootCategory: (v: Category | null) => void;
  setAttributes: (v: Attribute[]) => void;
  setManufacturerOptions: (v: any[]) => void;
  setModelOptions: (v: any[]) => void;
  setCurrentItem: (v: any) => void;
  setSelectedCondition: (v: string | null) => void;
  setCurrentAttributeIndex: (v: number) => void;
  setGoldCurrentAttributeIndex: (v: number) => void;
  showToast: (msg: string, retry?: () => void) => void;
};

export function useFetchHandlers({
  brand, goldParentCategory, categoryItemState, isEditing,
  categoriesFetchedRef, initialMountRef, restoringCategoryRef, itemTypeLabelRef,
  setLoading, setError, setCategories, setCategoryHistory,
  setSelectedCategoryParent, setSelectedRootCategory,
  setAttributes, setManufacturerOptions, setModelOptions,
  setCurrentItem, setSelectedCondition, setCurrentAttributeIndex, setGoldCurrentAttributeIndex,
  showToast,
}: FetchDeps) {
  const fetchData = useCallback(async (url: string, onSuccess: (data: any) => void) => {
    try {
      setLoading(true);
      const response = await DataRequest.execute(url, { method: "GET" }, { "Content-Type": "application/json" });
      if (response.error === "INVALID_ACCESS_TOKEN") return;
      if (response.data?.success) {
        onSuccess(response.data.payload);
        setError(null);
      } else {
        showToast("Error fetching data.", () => fetchData(url, onSuccess));
        setError(response.data?.message || "Failed to load data");
      }
    } catch (err) {
      showToast("Error fetching data.", () => fetchData(url, onSuccess));
      console.error("Error fetching data:", err);
      setError("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = (showLoader: boolean = false) => {
    if (initialMountRef.current && categoriesFetchedRef.current) return;
    if (!initialMountRef.current && categoriesFetchedRef.current) return;
    categoriesFetchedRef.current = true;
    if (showLoader) setLoading(true);
    fetchData("categories", (payload) => {
      const topLevelCategories: Category[] = payload || [];

      // On the very first fetch (page load), this runs in the same effect flush as
      // the brand-detection effect in ez-quote.tsx, before the `brand` state update
      // has propagated — so fall back to reading the DOM attribute directly here.
      const resolvedBrand =
        brand ?? document.querySelector("ez-quote")?.getAttribute("brand") ?? null;

      if (isJewelryOnlyBrand(resolvedBrand)) {
        const jewelry = topLevelCategories.find((c) => c.category_name === "Jewelry");
        if (jewelry?.childCategories?.length) {
          setCategories(jewelry.childCategories);
          setCategoryHistory([]);
          setSelectedCategoryParent(jewelry);
          setSelectedRootCategory(jewelry);
          if (initialMountRef.current) initialMountRef.current = false;
          if (showLoader) setLoading(false);
          return;
        }
      }

      setCategories(topLevelCategories);
      setCategoryHistory([]);
      setSelectedCategoryParent(null);
      setSelectedRootCategory(null);
      if (initialMountRef.current) initialMountRef.current = false;
      if (showLoader) setLoading(false);
    });
  };

  const resetAndFetchCategories = (showLoader = false) => {
    categoriesFetchedRef.current = false;
    initialMountRef.current = false;
    fetchCategories(showLoader);
  };

  const fetchManufacturers = (categoryId: number) => {
    fetchData(`categories/${categoryId}/manufacturers`, (payload) => {
      setManufacturerOptions(payload.manufacturers);
    });
  };

  const fetchModels = (categoryId: number, manufacturerId: string) => {
    if (!manufacturerId) return;
    fetchData(
      `categories/${categoryId}/manufacturers/${manufacturerId}/models`,
      (payload) => { setModelOptions(payload.models); }
    );
  };

  const fetchAttributes = (categoryId: number) => {
    const metalCategoryId = goldParentCategory?.category_id;
    fetchData(`categories/${categoryId}/attributes`, (payload) => {
      let fetchedAttributes: Attribute[] = payload.attributes.sort(
        (a: Attribute, b: Attribute) => a.orderId - b.orderId
      );

      if (metalCategoryId) {
        fetchedAttributes = fetchedAttributes
          .map((attr) => {
            if (attr.coreAttributeName === "Karat" || attr.coreAttributeName === "Quality") {
              return {
                ...attr,
                values: attr.values?.filter(
                  (v) => v.dependentAttributeValueID === String(metalCategoryId)
                ),
              };
            }
            return attr;
          })
          .filter(
            (attr) =>
              (attr.coreAttributeName !== "Karat" && attr.coreAttributeName !== "Quality") ||
              (attr.values?.length ?? 0) > 0
          );
      }

      const isGoldCategory = fetchedAttributes.some((a) => a.displayType === "SLIDER");
      if (isGoldCategory) {
        const hasBinaryAttr = fetchedAttributes.some(
          (a) =>
            a.displayType === "SINGLE_SELECT_LIST" &&
            a.values &&
            a.values.length <= 3 &&
            a.values.every((v) => ["yes", "no"].includes(v.coreValue.toLowerCase()))
        );
        if (!hasBinaryAttr) {
          fetchedAttributes.push({
            coreAttributeName: "Diamonds",
            displayType: "SINGLE_SELECT_LIST",
            orderId: 98,
            values: [{ coreValue: "Yes" }, { coreValue: "No" }],
          });
        }
        const hasTextArea = fetchedAttributes.some((a) => a.displayType === "TEXT_AREA");
        if (!hasTextArea) {
          fetchedAttributes.push({
            coreAttributeName: "Additional Details",
            displayType: "TEXT_AREA",
            orderId: 99,
          });
        }
      }

      setAttributes(fetchedAttributes);

      if (restoringCategoryRef.current) {
        const selectedCategory = restoringCategoryRef.current;
        const itemTypeLabel = itemTypeLabelRef.current;
        const saved = categoryItemState[selectedCategory.category_id];
        if (saved) {
          setCurrentItem({
            category: selectedCategory,
            attributes: saved.attributes,
            condition: saved.condition,
            itemTypeLabel,
          });
          setSelectedCondition(saved.condition ? saved.condition.id : null);
          const lastFilledIndex = fetchedAttributes
            .map((attr) => attr.coreAttributeName)
            .reduce((maxIdx, key, idx) => {
              const val = saved.attributes?.[key];
              return val && val.id && val.id !== "" ? idx : maxIdx;
            }, -1);
          setCurrentAttributeIndex(lastFilledIndex >= 0 ? lastFilledIndex : 0);
          if (saved.attributes?.["Manufacturer"]?.id) {
            fetchModels(selectedCategory.category_id, saved.attributes["Manufacturer"].id);
          }
        } else {
          setCurrentItem({
            category: selectedCategory,
            attributes: {},
            condition: null,
            itemTypeLabel,
          });
          setSelectedCondition(null);
          setCurrentAttributeIndex(0);
          setGoldCurrentAttributeIndex(0);
        }
        restoringCategoryRef.current = null;
        itemTypeLabelRef.current = undefined;
      } else if (!isEditing) {
        setCurrentAttributeIndex(0);
        setGoldCurrentAttributeIndex(0);
      }

      const manufacturerAttribute = fetchedAttributes.find(
        (attr) => attr.coreAttributeName === "Manufacturer"
      );
      if (manufacturerAttribute) {
        fetchManufacturers(categoryId);
      }
    });
  };

  return { fetchData, fetchCategories, resetAndFetchCategories, fetchAttributes, fetchManufacturers, fetchModels };
}
